import { parsePhoneNumber } from "libphonenumber-js";
import z from "zod/v4";

/**
 * Normalizes a phone number to E.164 format.
 * Accepts various formats like (415) 555-1234, 415-555-1234, +1 415 555 1234, etc.
 * Also handles international numbers with + prefix or 00 prefix.
 * Returns null if the phone number is invalid.
 */
export function normalizePhone(
  input: string,
  defaultCountry = "US" as const,
): string | null {
  // Handle international dialing prefix (00 -> +)
  let normalized = input;
  if (input.startsWith("00")) {
    normalized = "+" + input.slice(2);
  }

  try {
    // First try parsing as international (no default country)
    // This works for numbers with + prefix or after 00 conversion
    if (normalized.startsWith("+")) {
      const phone = parsePhoneNumber(normalized);
      if (phone?.isValid()) {
        return phone.format("E.164");
      }
    }

    // Fall back to default country parsing for numbers without country code
    const phone = parsePhoneNumber(input, defaultCountry);
    return phone?.isValid() ? phone.format("E.164") : null;
  } catch {
    return null;
  }
}

/**
 * Zod schema that validates phone numbers without transforming.
 * Use when you need to normalize manually (e.g., forms with dynamic validation).
 */
export const phoneValidationSchema = z
  .string()
  .nonempty({ message: "Phone number is required" })
  .refine((val) => normalizePhone(val) !== null, {
    message: "Invalid phone number. Please enter a valid US phone number.",
  });

/**
 * Zod schema that accepts phone numbers in various formats and normalizes them to E.164.
 * Use this instead of z.e164() for user input.
 */
export const phoneSchema = z
  .string()
  .nonempty({ message: "Phone number is required" })
  .transform((val, ctx) => {
    const normalized = normalizePhone(val);
    if (!normalized) {
      ctx.addIssue({
        code: "custom",
        message: "Invalid phone number. Please enter a valid US phone number.",
      });
      return z.NEVER;
    }
    return normalized;
  });

/**
 * Formats a phone number for display. Returns a human-readable international
 * format (e.g. "+1 415 555 1234") when possible, or empty string for
 * malformed / missing values.
 */
export function formatPhone(raw: string | null | undefined): string {
  if (!raw) return "";
  try {
    const phone = parsePhoneNumber(raw, "US");
    if (phone?.isValid()) {
      return phone.formatInternational();
    }
  } catch {
    // fall through
  }
  return raw;
}

/**
 * Checks if a phone number is valid (can be normalized to E.164).
 */
export function isValidPhone(
  input: string,
  defaultCountry = "US" as const,
): boolean {
  return normalizePhone(input, defaultCountry) !== null;
}
